//============================================================================
// Name        : PrintLabels.tsx
// Purpose     : Implement Warehouse recive print labels
// Created     : 2019-07-01
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

// tslint:disable-next-line: no-unused-expression
/** @jsx jsx */ jsx;
import { jsx } from "@emotion/core";
import axios from "axios";
import React from "react";
import { connect } from "react-redux";
import Select from "react-select";
import Switch from "react-switch";
import * as GlobalSettings from "../../globalSettings";
import { IFetchPrintersAction, ISetPrintStateAction, ISetSavePrinterAction, ISetSelectedPrinterAction } from "./actions/printerActions";
import { IFinishReceivingItemAction, ISetLabelVerifiedAction } from "./actions/receiveActions";
import ConfirmDialog from "./ConfirmDialog";
import PasswordDialog from "./PasswordDialog";
import ReceptionLabelsDetected from "./ReceptionLabelsDetected";
import ReprintLabels from "./ReprintLabels";
import { IPrinterModel, IPrinterOption, IPrintLabelsProps, IPrintState,  IReceptionItem, PrinterActions, ReceiveActions, TagType } from "./types";

declare var apiKey: string;
declare var apiUrl: string;
declare var storeLocation: string;
declare var App: any;

interface IPrintLabelState {
    showPasswordPrompt: boolean;
    forceShowAfterDetected: boolean;
}

class PrintLabels extends React.Component<IPrintLabelsProps, IPrintLabelState> {
    printStarted: boolean;
    epcReadsChannel: any;
    verificationDlg: React.RefObject<ReceptionLabelsDetected>;

    constructor(props: IPrintLabelsProps) {
        super(props);
        this.props.fetchPrinters(this.props.printerModels);
        this.epcReadsChannel = App.cable.subscriptions.create(
            // Connection configuration
            {
                channel: "InterceptEpcReadsChannel",
                roomId: `${storeLocation}`
            },
            // Pass in connection callbacks
            {
                connected() { /* Called when the subscription is ready for use on the server */ },
                disconnected() { /* Called when the subscription has been terminated by the server */ },
                received: (data: any) => this.onEpcsRead(data)
            }
        );

        this.verificationDlg = React.createRef();
        this.printStarted = false;

        this.state = {
            showPasswordPrompt: false,
            forceShowAfterDetected: false
        };
    }

    getClassName = () => this.props.show ? "modal show" : "modal fade";

    // Check the received data for our generated labels
    onEpcsRead = (data: { epcs: string[]; }) => {
        // Retrieve generated labels from item
        const item: Partial<IReceptionItem> = this.props.receptionItems.find(x => x.id == this.props.itemId);
        item.labels.filter(label => label.verified == false).map(label => {
            // For each label try to find a match on the read epcs, if we find a match, then
            // check what type of tag it is, and increment the counter for it.
            const foundTag = data.epcs.find(epc => epc == label.epc);
            if (foundTag) {
                this.props.setLabelVerified(this.props.itemId, label.epc);

                // NOTE ac[2019-10]: Per Vlad's request, after all tags have been confirmed
                // proceed with the continue action automatically.
                const detectedPalletCount = this.getDetectedPalletCount();
                const detectedCaseCount   = this.getDetectedCaseCount();
                const allDetected  = (detectedPalletCount == this.props.palletCount) && (detectedCaseCount == this.props.caseCount);
                if (item.labels.length > 0 && allDetected) {
                    this.onContinue();
                }
            }
        });
    }

    getDetectedPalletCount = (): number => {
        let detectedPalletCount = 0;
        const item: Partial<IReceptionItem> = this.props.receptionItems.find(x => x.id == this.props.itemId);
        item.labels.filter(label => (label.verified == true && label.tagType == TagType.Pallet)).map(() => detectedPalletCount += 1);
        return detectedPalletCount;
    }

    getDetectedCaseCount = (): number => {
        let detectedCaseCount = 0;
        const item: Partial<IReceptionItem> = this.props.receptionItems.find(x => x.id == this.props.itemId);
        item.labels.filter(label => (label.verified == true && label.tagType == TagType.Case)).map(() => detectedCaseCount += 1);
        return detectedCaseCount;
    }

    onVerificationContinue = () => {
        // Retrieve generated labels from item
        const item = this.props.receptionItems.find(x => x.id == this.props.itemId);
        const epcs = item.labels.map(label => {
            return label.epc;
        });
        this.props.finishReceivingItem({ id: item.id, epcs });
    }

    onBeginPrint = () => {
        if (this.printStarted) {
            return;
        }
        // Keep track that we're printing
        this.printStarted = true;
        this.props.setPrintState({ printing: true });

        // Force re-update verification dialog, in case it was closed and we're reopening the print dialog, it's state needs clearing.
        this.verificationDlg.current.reset();

        // Retrieve generated labels from item
        const item = this.props.receptionItems.find(x => x.id == this.props.itemId);
        console.log(item);

        // Send labels to the server to start printing
        const printData = {
            api_key:   apiKey,
            printerId: this.props.selectedPrinter.value,
            labels:    item.labels
        };
        axios.post(apiUrl + "/print-labels", printData);
    }

    onSavePrinterChange = (value: boolean) => this.props.setSavePrinter(value);
    onPrinterChange = (option: IPrinterOption) => {
        if (this.props.savePrinter) {
            this.props.saveDefaultPrinter(option);
        }
        else {
            this.props.setSelectedPrinter(option);
        }
    }

    onClose = () => {
        // Kill created tags when cancelling the print dialog
        const item: Partial<IReceptionItem> = this.props.receptionItems.find(x => x.id == this.props.itemId);
        const epcs = item.labels.map(l => l.epc);
        axios.post(apiUrl + "/kill-tags", { epcs, api_key: apiKey });

        // Force re-update verification dialog, in case it was closed and we're reopening the print dialog, it's state needs clearing.
        this.verificationDlg.current.reset();

        $(".receive-overlay-wrapper").fadeOut();
        this.props.cancelPrinting();
        this.props.setPrintState({ printing: false });
    }

    onCancel = () => this.onClose();
    onContinue = () => {
        // Retrieve generated labels from item
        const item = this.props.receptionItems.find(x => x.id == this.props.itemId);
        const epcs = item.labels.map(label => {
            return label.epc;
        });

        // Axios request to mark the tags live
        axios.post(apiUrl + "/finish-receiving-item", {
            api_key: apiKey,
            receivingId: this.props.reception.receiving_id,
            itemId: item.id,
            extReceiveId: this.props.reception.receiving_id,
            epcs,
            postingDate: this.props.postingDate,
            actualReceiptDate: this.props.actualReceiptDate
        }).catch(error => alert(error));

        this.setState({ forceShowAfterDetected: true });

        // Keep track that we're done printing
        this.props.setPrintState({ printing: false });
    }

    onOverrideContinue = () => this.setState({ showPasswordPrompt: true });
    onReprintLabels    = () => this.props.showReprintDialog();
    onPasswordConfirm  = () => this.onContinue();
    onPasswordCancel   = () => this.setState({ showPasswordPrompt: false });

    render() {
        const detectedPalletCount = this.getDetectedPalletCount();
        const detectedCaseCount   = this.getDetectedCaseCount();
        const continueBtnVisible  = (detectedPalletCount == this.props.palletCount) && (detectedCaseCount == this.props.caseCount);
        const scanning = !continueBtnVisible;

        return (
            <React.Fragment>
                <div className={this.getClassName()} tabIndex={-1} role="dialog">
                    <div className="modal-dialog" css={{ width: "800px" }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <button onClick={this.props.showConfirmCancelPrint} type="button" className="close" data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                <h1 className="modal-title">Print Labels</h1>
                            </div>
                            <div className="modal-body">
                                <div className="mf-printing-wrapper" css={{ display: "flex" }}>
                                    <div className="mf-print-dlg-printing" css={{ width: "45%", marginRight: "30px" }}>
                                    <div css={{ display: "inline-flex" }}>
                                        <img src="/images/printer.png" alt="Printer" css={{ height: "115px" }}/>
                                        <div>
                                            <div css={{ fontSize: "16px", fontWeight: "bold" }}>RFID Printer</div>
                                                <div css={{ width: "100%" }}>
                                                    <Select options={this.props.printers} onChange={this.onPrinterChange} value={this.props.selectedPrinter} styles={GlobalSettings.selectCustomStyle} />
                                                </div>
                                                <div css={{ marginTop: "10px", display: "flex", alignItems: "center" }}>
                                                    <Switch onChange={this.onSavePrinterChange} checked={this.props.savePrinter} />
                                                    <span css={{ fontSize: "16px", marginLeft: "10px" }}>Save printer as default</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div css={{ textAlign: "center" }}>
                                            <div css={{ fontWeight: "bold", fontSize: "130%", paddingTop: "10px", paddingBottom: "10px" }}>Pending Printing</div>
                                            <div>{this.props.palletCount} Pallet RFID Labels</div>
                                            <div>{this.props.caseCount} Individual Labels</div>

                                            <div css={{ marginTop: "15px" }}>
                                                {this.props.selectedPrinter && !this.props.printing && <button onClick={this.onBeginPrint} className="man-btn man-green-btn">Print Labels</button>}
                                                {!continueBtnVisible && <button onClick={this.props.showConfirmCancelPrint} className="man-btn man-red-btn">Cancel</button>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mf-print-dlg-validation" css={{ width: "50%", textAlign: "center" }}>
                                        <h1>Tag Printing Validation In-Progress</h1>
                                        {!continueBtnVisible && <div css={{ textAlign: "center", color: "red", marginTop: 15, fontWeight: "bold" }}>* DO NOT PROCEED ATTACHING TAG(S) *<br/>UNTIL PRINTED TAG(S) ARE VALIDATED BY READER</div>}

                                        <div css={{ display: "flex", marginTop: "20px", justifyContent: "center", marginLeft: "-20px" }}>
                                            {scanning && <div css={{ width: "120px" }}>
                                                <img css={{ height: "80px" }} src="/images/ripple.gif" alt=""/>
                                                <div css={{ textAlign: "center" }}>SCANNING FOR PRINTED TAGS</div>
                                            </div>}
                                            <div css={{ paddingTop: "10px" }}>
                                                <div>{detectedPalletCount} of {this.props.palletCount} Pallet Tags Detected</div>
                                                <div>{detectedCaseCount} of {this.props.caseCount} Individual Tags Detected</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" css={{ padding: "15px" }}>
                                <div className="actions-footer" css={{ display: "flex", justifyContent: "space-between" }}>
                                    <button onClick={this.onReprintLabels}>Reprint / View Missing Tags</button>
                                    <button onClick={this.onOverrideContinue}>Continue Override</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <ConfirmDialog
                        show={this.props.showCancelConfirm}
                        title={this.props.confirmationTitle}
                        message={this.props.confirmationMsg}
                        positiveBtnLbl="Yes"
                        negativeBtnLbl="No"
                        positiveBtnCallback={this.onCancel}
                        negativeBtnCallback={this.props.hideCancelConfirmation} />

                {/* All tags detected dialog */}
                <ReceptionLabelsDetected ref={this.verificationDlg}
                    show={(this.props.show && continueBtnVisible) || this.state.forceShowAfterDetected}
                    callback={this.onVerificationContinue} />

                <PasswordDialog show={this.state.showPasswordPrompt}
                                validatePasswordUrl={this.props.validatePasswordUrl}
                                positiveCallback={this.onPasswordConfirm}
                                negativeCallback={this.onPasswordCancel} />

                <ReprintLabels itemId={this.props.itemId} />
            </React.Fragment>
        );
    }
}

const mapStateToProps = state => ({
    show:                state.receiveOrders.showPrintDialog,
    validatePasswordUrl: state.receiveOrders.validatePasswordUrl,
    printers:            state.printers.items,
    printerModels:       state.receiveOrders.printers,
    reception:           state.receiveOrders.reception,
    actualReceiptDate:   state.receiveOrders.actualReceiptDate,
    postingDate:         state.receiveOrders.postingDate,
    receptionItems:      state.receiveOrders.reception.external_receive_items,

    showCancelConfirm:   state.receiveOrders.showCancelPrintConfirm,
    confirmationTitle:   state.receiveOrders.confirmationTitle,
    confirmationMsg:     state.receiveOrders.confirmationMsg,

    savePrinter:         state.printers.savePrinter,
    defaultPrinter:      state.printers.defaultPrinter,
    selectedPrinter:     state.printers.selectedPrinter,

    // Printing status props
    palletCount:         state.printers.palletCount,
    caseCount:           state.printers.caseCount,
    detectedPalletCount: state.printers.detectedPalletCount,
    detectedCaseCount:   state.printers.detectedCaseCount,
    printing:            state.printers.printing
});
const mapDispatchToProps = dispatch => ({
    fetchPrinters: (printers: IPrinterModel[]) => dispatch({
        type: PrinterActions.FetchPrinters,
        printers
    } as IFetchPrintersAction),
    setSavePrinter: (value: boolean) => dispatch({
        type: PrinterActions.SetSavePrinter,
        value
    } as ISetSavePrinterAction),
    saveDefaultPrinter: (printer: IPrinterOption) => dispatch({
        type: PrinterActions.SaveDefaultPrinter,
        printer
    }),
    setSelectedPrinter: (printer: IPrinterOption) => dispatch({
        type: PrinterActions.SetSelectedPrinter,
        printer
    } as ISetSelectedPrinterAction),
    setPrintState: (props: Partial<IPrintState>) => dispatch({
        type: PrinterActions.SetPrintState,
        payload: { props }
    } as ISetPrintStateAction),
    showConfirmCancelPrint: () => dispatch({
        type: ReceiveActions.ConfirmCancelPrint
    }),
    hideCancelConfirmation: () => dispatch({
        type: ReceiveActions.HideCancelPrint
    }),
    cancelPrinting: () => dispatch({
        type: ReceiveActions.CancelPrinting
    }),
    setLabelVerified: (itemId: number, epc: string) => dispatch({
        type: ReceiveActions.SetLabelVerified,
        payload: {
            itemId,
            epc
        }
    } as ISetLabelVerifiedAction),
    finishReceivingItem: (props: object) => dispatch({
        type: ReceiveActions.FinishReceivingItem,
        payload: { ...props }
    } as IFinishReceivingItemAction),
    showReprintDialog: () => dispatch({
        type: ReceiveActions.ShowReprintDialog
    })
});

export default connect(mapStateToProps, mapDispatchToProps)(PrintLabels);
